import { type Package, PackageTier } from '@qdrant/qdrant-cloud-public-api/booking/v1';
import { type ClusterValid } from '@qdrant/qdrant-cloud-public-api/cluster/v1';
import { type QueryClient } from '@tanstack/react-query';
import { BOOKING_PACKAGE_TYPE_FREE, BookingServices } from '@/api/services/booking';
import { reportError } from '@/helpers/errors/report';
import { BOOKING_STATUS } from '@/utils/booking-utils';

/**
 * Check if the given cluster is a free tier
 */
export function isFreeTierBookingPackage(packageInfo?: Package) {
  return packageInfo?.type === BOOKING_PACKAGE_TYPE_FREE;
}

export function isPremiumTierBookingPackage(packageInfo?: Package) {
  return packageInfo?.tier === PackageTier.PREMIUM;
}

/**
 * Get package information for an existing cluster by querying the API
 */
export function getClusterPackageQueryOptions(cluster: ClusterValid) {
  return {
    accountId: cluster.accountId,
    cloudProviderId: cluster.cloudProviderId,
    cloudProviderRegionId: cluster.cloudProviderRegionId,
    // Get both active and deactivated packages because the cluster might have been
    // created with a package that is deactivated now
    statuses: [BOOKING_STATUS.ACTIVE, BOOKING_STATUS.DEACTIVATED],
  };
}

/**
 * Find package information for a cluster from the packages list using the cluster's package ID
 */
export function findClusterPackage(packages: Package[], cluster: ClusterValid): Package | undefined {
  if (!cluster.configuration?.packageId) {
    return undefined;
  }

  return packages.find((pkg) => pkg.id === cluster.configuration.packageId);
}

/**
 * Check if a cluster is free tier by fetching its package information from the API
 * This function fetches packages internally and determines if the cluster is free tier.
 *
 * @param cluster - The cluster to check
 * @param queryClient - Required QueryClient instance for API calls
 * @returns Promise<boolean> indicating if the cluster is free tier, or false if package info cannot be determined
 */
export async function isClusterFreeTier(cluster: ClusterValid, queryClient: QueryClient): Promise<boolean> {
  try {
    const params = getClusterPackageQueryOptions(cluster);
    const queryOptions = BookingServices.listPackagesQuery(params);
    const packagesResponse = await queryClient.fetchQuery(queryOptions);
    const packages = packagesResponse?.items ?? [];

    const packageInfo = findClusterPackage(packages, cluster);
    return isFreeTierBookingPackage(packageInfo);
  } catch (error) {
    reportError(error, { domain: 'billing', action: 'check-cluster-free-tier' });
    return false;
  }
}

/**
 * Checks asynchronously if a cluster is premium tier by fetching package information
 * @param cluster - The cluster to check
 * @param queryClient - Required QueryClient instance for API calls
 * @returns Promise<boolean> indicating if the cluster is premium tier, or false if package info cannot be determined
 */
export async function isClusterPremiumTier(cluster: ClusterValid, queryClient: QueryClient): Promise<boolean> {
  try {
    const params = getClusterPackageQueryOptions(cluster);
    const queryOptions = BookingServices.listPackagesQuery(params);
    const packagesResponse = await queryClient.fetchQuery(queryOptions);
    const packages = packagesResponse?.items ?? [];

    const packageInfo = findClusterPackage(packages, cluster);
    return isPremiumTierBookingPackage(packageInfo);
  } catch (error) {
    reportError(error, { domain: 'billing', action: 'check-cluster-premium-tier' });
    return false;
  }
}
